import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Button, IconButton, CircularProgress, 
    Container, Chip, Stack, Dialog, DialogTitle, DialogContent, 
    Divider, MenuItem, Select, FormControl, InputLabel, TextField, Paper,
    DialogActions,
    Grid
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faHistory, faCheck, faFilter, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import { getDispatches, updateDispatchStatus } from '../../../controllers/help/DispatchController';
import ResponseComponent from '../../../components/responseComponent';

export default function DispatchControlView() {
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [dispatches, setDispatches] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    
    // Estados del Dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [selected, setSelected] = useState(null);
    const [newStatus, setNewStatus] = useState('');
    const [note, setNote] = useState('');

    const [updating, setUpdating] = useState(false);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        setLoading(true);
        const response = await getDispatches();
        if (response?.success) setDispatches(response.data.data);
        setLoading(false);
    };

    const handleUpdate = async () => {
        // Validación de seguridad antes de cualquier llamado
        if (!note || note.trim().length < 5) {
            alert("¡Atención! La nota de auditoría es obligatoria y debe ser detallada.");
            return;
        }

        setUpdating(true);

        try {
            const response = await updateDispatchStatus(selected.id, { status: newStatus, note });
            
            if (response?.success) {
                setOpenDialog(false);
                setNote('');
                loadData();
            } else {
                // El alert aquí es útil porque detiene el flujo y notifica el error del servidor
                alert("Error del servidor: " + (response?.message || "No se pudo actualizar el estatus."));
            }
        } catch (err) {
            alert("Error crítico de conexión. Por favor, verifica tu internet e intenta de nuevo.");
        } finally {
            setUpdating(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 5, pb: 6, bgcolor:'transparent'}}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={8} lg={10}>
                    <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-1px' }}>Despachos</Typography>
                    <Typography variant="body2" color="text.secondary">Gestión operativa en tiempo real</Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={2}>
                    <Button 
                        variant="contained" startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => history.push('/dispatches/create')}
                        sx={{ bgcolor: '#000', borderRadius: 2, px: 3, py: 1.5, fontWeight: 700, '&:hover': { bgcolor: '#333' } }}
                    >
                        Nuevo Despacho
                    </Button>
                </Grid>
                {/* Filtros Integrados y Funcionales */}
                <Grid item xs={12} sm={12} md={12} lg={12}>
                    <Paper 
                        elevation={0} 
                        sx={{ 
                            p: 2, 
                            mb: 4, 
                            border: '1px solid #e0e0e0', 
                            borderRadius: 3, 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, // Reducimos el gap para que quepan mejor
                            overflowX: 'hidden' // Importante para el contenedor principal
                        }}
                    >
                        <FontAwesomeIcon icon={faFilter} color="#999" style={{ minWidth: '20px' }} />
                        
                        {/* Este es el contenedor que soluciona el desborde */}
                        <Box sx={{ 
                            display: 'flex', 
                            overflowX: 'auto', 
                            gap: 1, 
                            pb: 0.5,
                            width: '100%',
                            '&::-webkit-scrollbar': { display: 'none' } // Oculta la barra de scroll
                        }}>
                            {['all', 'en_ruta', 'entregado', 'desviado'].map((status) => (
                                <Chip 
                                    key={status} 
                                    label={status.replace('_', ' ').toUpperCase()} 
                                    clickable
                                    onClick={() => setStatusFilter(status)}
                                    variant={statusFilter === status ? "filled" : "outlined"}
                                    color="primary"
                                    sx={{ 
                                        borderRadius: 2, 
                                        fontWeight: 700,
                                        flexShrink: 0 // ¡ESTO ES LA CLAVE! Evita que los chips se compriman
                                    }}
                                />
                            ))}
                        </Box>
                    </Paper>
                </Grid>
                
                {/* Grid de Despachos */}
                {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} /> : (
                    <>
                        {dispatches.filter(d => statusFilter === 'all' || d.status === statusFilter).map((d) => (
                            <Grid item xs={12}>
                                <Paper key={d.id} elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 4, transition: '0.3s', '&:hover': { borderColor: '#000' } }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography sx={{ fontWeight: 900, fontSize: '1.1rem' }}>{d.tracking_code}</Typography>
                                        <Chip label={d.status} color={d.status === 'en_ruta' ? 'warning' : 'success'} size="small" />
                                    </Box>
                                    <Typography sx={{ fontWeight: 600 }}>{d.destination_city}</Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>{d.delivery_point}</Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800 }}>{`${d.dispatcher?.name || ''} ${d.dispatcher?.last_name || ''}`}</Typography>
                                        <Button size="small" variant="contained" disableElevation onClick={() => { setSelected(d); setNewStatus(d.status); setOpenDialog(true); }}>Gestionar</Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        ))}
                    </>
                )}
            </Grid>

            

            

            

            {/* Dialog de Gestión */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 900, pr: 6 }}>
                    Gestión: {selected?.tracking_code}
                    <IconButton 
                        onClick={() => setOpenDialog(false)} 
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <FontAwesomeIcon icon={faTimes} size="sm" />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent dividers>
                    {/* BLOQUE UNIFICADO: Datos de Contexto Operativo */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 900, color: '#666', mb: 1, display: 'block' }}>DATOS OPERATIVOS</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Vehículo: {selected?.vehicle?.plate} ({selected?.vehicle?.type})</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Categoría: {selected?.vehicle?.category}</Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>Chofer: {selected?.driver_name}</Typography>
                        <Typography variant="body2">C.I: {selected?.driver_ci} | Tel: {selected?.driver_phone}</Typography>
                    </Box>

                    {/* BLOQUE UNIFICADO: Detalle de Carga (Mismo estilo gris) */}
                    <Typography variant="caption" sx={{ fontWeight: 900, color: '#666', mb: 1, display: 'block' }}>DETALLE DE CARGA</Typography>
                    <Box 
                        sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, mb: 3 }} 
                        dangerouslySetInnerHTML={{ __html: selected?.cargo_description }} 
                    />

                    {/* Gestión de Estatus */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Estatus</InputLabel>
                        <Select value={newStatus} label="Estatus" onChange={(e) => setNewStatus(e.target.value)}>
                            <MenuItem value="en_ruta">En Ruta</MenuItem>
                            <MenuItem value="entregado">Entregado</MenuItem>
                            <MenuItem value="desviado">Desviado</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField 
                        fullWidth multiline rows={3} label="Observaciones (Obligatorio)" 
                        value={note} onChange={(e) => setNote(e.target.value)} 
                        sx={{ mb: 3 }}
                    />
                    
                    {/* Historial */}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800 }}>Historial de Cambios</Typography>
                    <Box sx={{ maxHeight: 150, overflowY: 'auto', p: 1, border: '1px solid #eee', borderRadius: 2 }}>
                        {selected?.status_logs?.map((log, i) => (
                            <Typography key={i} variant="caption" sx={{ display: 'block', mb: 0.5, color: '#555' }}>
                                • {log.timestamp} | {log.action} {log.user_name ? `por ${log.user_name}` : ''}
                            </Typography>
                        ))}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setOpenDialog(false)} 
                        disabled={updating} 
                        color="inherit" 
                        sx={{ fontWeight: 600 }}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleUpdate} 
                        disabled={updating} 
                        sx={{ 
                            bgcolor: '#000', 
                            borderRadius: 2, 
                            px: 4,
                            minWidth: '160px',
                            height: '40px' 
                        }}
                    >
                        {updating ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Guardar Cambios'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}